import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import clsx from 'clsx'
import { AnimatePresence, motion } from 'framer-motion'
import he from 'he'
import React, { useRef, useState } from 'react'
import { useDebounce } from 'react-use'
import { useTranslations } from '../../hooks/useTranslations'
import { useToolOverlay } from '../Context/ToolOverlayContext'

function removeDiacritics(str) {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

function highlightMatch(text, query) {
  const textClean = removeDiacritics(text.toLowerCase())
  const queryClean = removeDiacritics(query.toLowerCase())

  const startIndex = textClean.indexOf(queryClean)
  if (startIndex === -1) {
    return text
  }

  const endIndex = startIndex + queryClean.length

  return (
    <>
      {text.slice(0, startIndex)}
      <b>{text.slice(startIndex, endIndex)}</b>
      {text.slice(endIndex)}
    </>
  )
}

const InlinePlzTool = () => {
  const inputRef = useRef(null)
  const {
    plz,
    setPlz,
    people,
    setPeople,
    handleSubmit,
    preload,
    findCityName,
    setContextSectionLimit,
    setToolOverlayOpen,
  } = useToolOverlay()
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const { data: autocompleteEntries, isSuccess } = useQuery(
    ['autocomplete', debouncedQuery],
    () => axios.get(`/postcodes/api?q=${debouncedQuery}`).then(res => res.data),
    { enabled: debouncedQuery.length >= 3 },
  )
  const translations = useTranslations()

  const preloadData = () => {
    preload()
  }

  const onSubmit = e => {
    if (!plz && autocompleteEntries.length > 0) {
      setPlz(autocompleteEntries[0].postcode)
      const cityName = findCityName(autocompleteEntries[0].postcode)
      if (cityName) {
        setQuery(`${autocompleteEntries[0].postcode}`)
      }
      handleSubmit(e, autocompleteEntries[0].postcode)
    }

    inputRef.current?.blur()

    e.preventDefault()

    setQuery('')
    setContextSectionLimit('people')
    setToolOverlayOpen(true)
  }

  useDebounce(() => setDebouncedQuery(query), 10, [query])

  return (
    <form action="#" onSubmit={onSubmit}>
      <div className="relative inline-flex">
        <motion.label
          className="absolute z-10 inline-flex px-1 pointer-events-none left-2.5 top-[11px]"
          style={{
            fontSize: query.length > 0 ? 10 : 16,
            backgroundColor: query.length > 0 ? '#F5F5F5' : 'transparent',
          }}
          animate={{
            y: query.length > 0 ? -18 : 0,
          }}
          transition={{ type: 'ease' }}
        >
          {translations.plz}
        </motion.label>
        <div className="relative inline-flex">
          <input
            ref={inputRef}
            name="postcode"
            type="text"
            autoComplete="off"
            className={clsx(
              'w-40 leading-6 border bg-transparent border-gray-4 appearance-none focus:outline-none focus:border-black focus:border focus:ring-0 transition-all focus:w-[240px]',
              [
                isSuccess &&
                autocompleteEntries.length > 0 &&
                people.length === 0
                  ? 'rounded-tl rounded-tr'
                  : 'rounded',
              ],
            )}
            value={query}
            onFocus={preloadData}
            onChange={e => {
              setPeople([])
              // setPlz(e.target.value)
              setPlz('')
              setQuery(e.target.value)
            }}
            style={{ width: query.length > 0 ? 240 : undefined }}
          />
          {isSuccess &&
            people.length === 0 &&
            autocompleteEntries &&
            autocompleteEntries?.filter(({ postcode }) =>
              findCityName(postcode),
            ).length > 0 && (
              <div className="absolute left-0 right-0 top-full max-h-[300px] overflow-y-auto border-x border-b border-black rounded-bl rounded-br">
                {autocompleteEntries
                  .filter(({ postcode }) => findCityName(postcode))
                  .map(({ postcode, city }) => {
                    return (
                      <button
                        key={`${postcode}-${city}`}
                        type="text"
                        className="flex items-center w-full gap-2 text-left transition-colors border-none hover:bg-gray-3 active:bg-gray-3 focus:bg-gray-3 focus:outline-none focus:border-none focus:ring-0"
                        onClick={e => {
                          // setCityName(findCityName(postcodeStr))
                          setPlz(postcode)
                          handleSubmit(e, postcode)

                          setQuery(`${postcode}`)
                        }}
                      >
                        <div className="tabular-nums">
                          {highlightMatch(String(postcode), debouncedQuery)}
                        </div>
                        <div className="truncate">
                          {highlightMatch(he.decode(city), debouncedQuery)}
                        </div>
                      </button>
                    )
                  })}
              </div>
            )}
          {debouncedQuery.length > 0 && findCityName(plz)?.length > 0 && (
            <div
              className="absolute top-0 bottom-0 h-[42px] leading-none flex items-center overflow-hidden text-black pointer-events-none whitespace-nowrap overflow-ellipsis u-textinput-overlay"
              style={{
                left: `calc(12px + ${`${plz}`.length + 1}ch)`,
                maxWidth: `calc(240px - 60px - ${`${plz}`.length + 1}ch)`,
              }}
            >
              {findCityName(plz)}
            </div>
          )}
        </div>
        <AnimatePresence>
          {query.length > 0 && (
            <motion.button
              key="submit-btn"
              type="submit"
              className="absolute right-[7px] top-[7px] z-10  focus:outline-none rounded-full"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <svg
                width="30"
                height="30"
                viewBox="0 0 30 30"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="15" cy="15" r="15" fill="black" />
                <path
                  d="M17 8L24 15M24 15L17 22M24 15H6"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    </form>
  )
}

export default InlinePlzTool
